import { cn } from "@/lib/cn";
import { Icons } from "@/components/ui/icons";
import { ProjectAvatar } from "@/workshop/components/panels/project-avatar";
import {
  ContextMenu,
  ContextMenuTrigger,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuSeparator,
} from "@/workshop/components/ui/context-menu";
import {
  ColorSubmenuItem,
  MoveSubmenuItem,
  SpaceIcon,
} from "./browse-tree-submenus";

/**
 * Workshop: BrowseTree — two-level project/space hierarchy for the Browse tab.
 *
 * Renders a sorted list of projects (pinned first, then alphabetical).
 * Each project expands to reveal its spaces. Both levels have context menus
 * with full CRUD actions.
 */

/* ---------- Interfaces ---------- */

interface BrowseProject {
  id: string;
  name: string;
  color: string;
  isPinned?: boolean;
  expanded?: boolean;
  spaces: BrowseSpace[];
}

interface BrowseSpace {
  id: string;
  name: string;
  isPinned?: boolean;
  active?: boolean;
}

interface BrowseTreeProps {
  projects: BrowseProject[];
  onSelectSpace?: (projectId: string, spaceId: string) => void;
  onToggleProject?: (projectId: string) => void;
  onNewProject?: () => void;
  onRenameProject?: (projectId: string) => void;
  onSetColorProject?: (projectId: string) => void;
  onPinProject?: (projectId: string) => void;
  onProjectSettings?: (projectId: string) => void;
  onNewSpace?: (projectId: string) => void;
  onDeleteProject?: (projectId: string) => void;
  onRenameSpace?: (projectId: string, spaceId: string) => void;
  onSetColorSpace?: (projectId: string, spaceId: string) => void;
  onPinSpace?: (projectId: string, spaceId: string) => void;
  onOrganizeFrames?: (projectId: string, spaceId: string) => void;
  onMoveSpace?: (
    fromProjectId: string,
    spaceId: string,
    toProjectId: string,
  ) => void;
  onDeleteSpace?: (projectId: string, spaceId: string) => void;
}

/* ---------- Helpers ---------- */

/** Sort: pinned first, then alphabetical by name. */
function sortByPinnedThenName<T extends { name: string; isPinned?: boolean }>(
  items: T[],
): T[] {
  return [...items].sort((a, b) => {
    if (a.isPinned && !b.isPinned) return -1;
    if (!a.isPinned && b.isPinned) return 1;
    return a.name.localeCompare(b.name);
  });
}

/* ---------- SpaceRow ---------- */

interface SpaceRowProps {
  projectId: string;
  projectColor: string;
  space: BrowseSpace;
  moveCandidates: BrowseProject[];
  onSelect?: (projectId: string, spaceId: string) => void;
  onRename?: (projectId: string, spaceId: string) => void;
  onSetColor?: (projectId: string, spaceId: string) => void;
  onPin?: (projectId: string, spaceId: string) => void;
  onOrganizeFrames?: (projectId: string, spaceId: string) => void;
  onMove?: (
    fromProjectId: string,
    spaceId: string,
    toProjectId: string,
  ) => void;
  onDelete?: (projectId: string, spaceId: string) => void;
}

function SpaceRow({
  projectId,
  projectColor,
  space,
  moveCandidates,
  onSelect,
  onRename,
  onSetColor,
  onPin,
  onOrganizeFrames,
  onMove,
  onDelete,
}: SpaceRowProps) {
  return (
    <ContextMenu>
      <ContextMenuTrigger>
        <button
          type="button"
          className={cn(
            "group flex w-full items-center gap-2 rounded-md py-1 pl-8 pr-2 text-left text-[11px] transition-colors",
            space.active
              ? "border-l-2 border-accent bg-accent-muted pl-[30px] text-primary"
              : "border-l-2 border-transparent text-secondary hover:bg-glass-fill-heavy hover:text-primary",
          )}
          onClick={() => onSelect?.(projectId, space.id)}
          data-testid={`browse-tree-space-${space.id}`}
          aria-current={space.active ? "page" : undefined}
        >
          <SpaceIcon
            className={cn(space.active ? "text-accent" : "text-muted")}
            color={projectColor}
          />
          <span className="flex-1 truncate">{space.name}</span>
          {space.isPinned && (
            <Icons.Pin
              className="h-2.5 w-2.5 shrink-0 text-muted opacity-60"
              aria-label="Pinned"
            />
          )}
        </button>
      </ContextMenuTrigger>
      <ContextMenuContent>
        <ContextMenuItem
          onClick={() => onRename?.(projectId, space.id)}
          data-testid={`browse-tree-space-${space.id}-rename`}
        >
          <Icons.Edit className="h-3 w-3" />
          Rename
        </ContextMenuItem>
        <ContextMenuItem
          onClick={() => onSetColor?.(projectId, space.id)}
          data-testid={`browse-tree-space-${space.id}-set-color`}
        >
          <Icons.Palette className="h-3 w-3" />
          Set color
        </ContextMenuItem>
        <ContextMenuItem
          onClick={() => onPin?.(projectId, space.id)}
          data-testid={`browse-tree-space-${space.id}-pin`}
        >
          <Icons.Pin className="h-3 w-3" />
          {space.isPinned ? "Unpin" : "Pin to top"}
        </ContextMenuItem>
        <ContextMenuItem
          onClick={() => onOrganizeFrames?.(projectId, space.id)}
          data-testid={`browse-tree-space-${space.id}-organize-frames`}
        >
          <Icons.Layers className="h-3 w-3" />
          Organize frames
        </ContextMenuItem>
        <MoveSubmenuItem
          candidates={moveCandidates}
          onSelect={(toProjectId) => onMove?.(projectId, space.id, toProjectId)}
          data-testid={`browse-tree-space-${space.id}-move`}
        />
        <ContextMenuSeparator />
        <ContextMenuItem
          destructive
          onClick={() => onDelete?.(projectId, space.id)}
          data-testid={`browse-tree-space-${space.id}-delete`}
        >
          <Icons.Trash2 className="h-3 w-3" />
          Delete
        </ContextMenuItem>
      </ContextMenuContent>
    </ContextMenu>
  );
}

/* ---------- ProjectRow ---------- */

interface ProjectRowProps {
  project: BrowseProject;
  allProjects: BrowseProject[];
  onToggle?: (projectId: string) => void;
  onSelectSpace?: (projectId: string, spaceId: string) => void;
  onRenameProject?: (projectId: string) => void;
  onSetColorProject?: (projectId: string) => void;
  onPinProject?: (projectId: string) => void;
  onProjectSettings?: (projectId: string) => void;
  onNewSpace?: (projectId: string) => void;
  onDeleteProject?: (projectId: string) => void;
  onRenameSpace?: (projectId: string, spaceId: string) => void;
  onSetColorSpace?: (projectId: string, spaceId: string) => void;
  onPinSpace?: (projectId: string, spaceId: string) => void;
  onOrganizeFrames?: (projectId: string, spaceId: string) => void;
  onMoveSpace?: (
    fromProjectId: string,
    spaceId: string,
    toProjectId: string,
  ) => void;
  onDeleteSpace?: (projectId: string, spaceId: string) => void;
}

function ProjectRow({
  project,
  allProjects,
  onToggle,
  onSelectSpace,
  onRenameProject,
  onSetColorProject,
  onPinProject,
  onProjectSettings,
  onNewSpace,
  onDeleteProject,
  onRenameSpace,
  onSetColorSpace,
  onPinSpace,
  onOrganizeFrames,
  onMoveSpace,
  onDeleteSpace,
}: ProjectRowProps) {
  const sortedSpaces = sortByPinnedThenName(project.spaces);
  const moveCandidates = allProjects.filter((p) => p.id !== project.id);

  return (
    <div data-testid={`browse-tree-project-${project.id}`}>
      {/* Project header row */}
      <ContextMenu>
        <ContextMenuTrigger>
          <button
            type="button"
            className="group flex w-full items-center gap-2 rounded-md px-2 py-1 text-left text-[11px] font-medium text-primary transition-colors hover:bg-glass-fill-heavy"
            onClick={() => onToggle?.(project.id)}
            data-testid={`browse-tree-project-${project.id}-toggle`}
            aria-expanded={project.expanded}
          >
            <Icons.ChevronRight
              className={cn(
                "h-3 w-3 shrink-0 text-muted transition-transform duration-150",
                project.expanded && "rotate-90",
              )}
              aria-hidden="true"
            />
            <ProjectAvatar
              name={project.name}
              color={project.color}
              size="sm"
              data-testid={`browse-tree-project-${project.id}-avatar`}
            />
            <span className="flex-1 truncate">{project.name}</span>
            {project.isPinned && (
              <Icons.Pin
                className="h-2.5 w-2.5 shrink-0 text-muted opacity-60"
                aria-label="Pinned"
              />
            )}
          </button>
        </ContextMenuTrigger>
        <ContextMenuContent>
          <ContextMenuItem
            onClick={() => onRenameProject?.(project.id)}
            data-testid={`browse-tree-project-${project.id}-rename`}
          >
            <Icons.Edit className="h-3 w-3" />
            Rename
          </ContextMenuItem>
          <ColorSubmenuItem
            currentColor={project.color}
            onSelect={(_color) => onSetColorProject?.(project.id)}
            data-testid={`browse-tree-project-${project.id}-set-color`}
          />
          <ContextMenuItem
            onClick={() => onPinProject?.(project.id)}
            data-testid={`browse-tree-project-${project.id}-pin`}
          >
            <Icons.Pin className="h-3 w-3" />
            {project.isPinned ? "Unpin" : "Pin to top"}
          </ContextMenuItem>
          <ContextMenuItem
            onClick={() => onProjectSettings?.(project.id)}
            data-testid={`browse-tree-project-${project.id}-settings`}
          >
            <Icons.Settings className="h-3 w-3" />
            Project settings
          </ContextMenuItem>
          <ContextMenuSeparator />
          <ContextMenuItem
            onClick={() => onNewSpace?.(project.id)}
            data-testid={`browse-tree-project-${project.id}-new-space`}
          >
            <Icons.Plus className="h-3 w-3" />
            New space
          </ContextMenuItem>
          <ContextMenuSeparator />
          <ContextMenuItem
            destructive
            onClick={() => onDeleteProject?.(project.id)}
            data-testid={`browse-tree-project-${project.id}-delete`}
          >
            <Icons.Trash2 className="h-3 w-3" />
            Delete
          </ContextMenuItem>
        </ContextMenuContent>
      </ContextMenu>

      {/* Spaces list — visible when project is expanded */}
      {project.expanded && sortedSpaces.length > 0 && (
        <div
          className="flex flex-col"
          data-testid={`browse-tree-project-${project.id}-spaces`}
        >
          {sortedSpaces.map((space) => (
            <SpaceRow
              key={space.id}
              projectId={project.id}
              projectColor={project.color}
              space={space}
              moveCandidates={moveCandidates}
              onSelect={onSelectSpace}
              onRename={onRenameSpace}
              onSetColor={onSetColorSpace}
              onPin={onPinSpace}
              onOrganizeFrames={onOrganizeFrames}
              onMove={onMoveSpace}
              onDelete={onDeleteSpace}
            />
          ))}
        </div>
      )}

      {/* Expanded but no spaces */}
      {project.expanded && sortedSpaces.length === 0 && (
        <p
          className="py-1 pl-8 text-[10px] text-muted"
          data-testid={`browse-tree-project-${project.id}-empty`}
        >
          No spaces yet
        </p>
      )}
    </div>
  );
}

/* ---------- BrowseTree ---------- */

export function BrowseTree({
  projects,
  onSelectSpace,
  onToggleProject,
  onNewProject,
  onRenameProject,
  onSetColorProject,
  onPinProject,
  onProjectSettings,
  onNewSpace,
  onDeleteProject,
  onRenameSpace,
  onSetColorSpace,
  onPinSpace,
  onOrganizeFrames,
  onMoveSpace,
  onDeleteSpace,
}: BrowseTreeProps) {
  const sortedProjects = sortByPinnedThenName(projects);

  return (
    <div
      className="flex flex-col"
      data-testid="browse-tree"
      role="tree"
      aria-label="Projects and spaces"
    >
      {/* Project rows */}
      <div className="flex flex-col gap-0.5 px-1 py-1">
        {sortedProjects.map((project) => (
          <ProjectRow
            key={project.id}
            project={project}
            allProjects={sortedProjects}
            onToggle={onToggleProject}
            onSelectSpace={onSelectSpace}
            onRenameProject={onRenameProject}
            onSetColorProject={onSetColorProject}
            onPinProject={onPinProject}
            onProjectSettings={onProjectSettings}
            onNewSpace={onNewSpace}
            onDeleteProject={onDeleteProject}
            onRenameSpace={onRenameSpace}
            onSetColorSpace={onSetColorSpace}
            onPinSpace={onPinSpace}
            onOrganizeFrames={onOrganizeFrames}
            onMoveSpace={onMoveSpace}
            onDeleteSpace={onDeleteSpace}
          />
        ))}

        {sortedProjects.length === 0 && (
          <p
            className="px-2 py-3 text-center text-[10px] text-muted"
            data-testid="browse-tree-empty"
          >
            No projects yet
          </p>
        )}
      </div>

      {/* New project button */}
      <div className="border-t border-glass-stroke-subtle px-2 py-1.5">
        <button
          type="button"
          className="flex w-full items-center gap-1.5 rounded-md px-1 py-1 text-[11px] text-muted transition-colors hover:text-secondary"
          onClick={onNewProject}
          data-testid="browse-tree-new-project"
          aria-label="Create new project"
        >
          <Icons.Plus className="h-3 w-3 shrink-0" />
          New project
        </button>
      </div>
    </div>
  );
}

export type { BrowseTreeProps, BrowseProject, BrowseSpace };
